/*
 * iLOLA — Gift Arrival Date, injected into the Rebuy Smart Cart drawer
 * ----------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * The "WHEN SHOULD IT ARRIVE?" fields already exist on the native /cart
 * page (snippets/gift-arrival-date.liquid) and write three order-level
 * cart attributes: "Gift Arrival Date", "Gift Timing", "Gift Special
 * Request". Customers never see /cart — the real checkout path is
 * PDP -> Rebuy Smart Cart drawer -> Shopify Checkout — so those fields
 * were never actually being shown or captured.
 *
 * Rebuy's Smart Cart already contains custom content (the "MAKE IT
 * PERSONAL" / Physical Presentation block, id="ilola-mip", and "THE
 * CONCIERGE EDIT" cross-sell, id="ilola-concierge"), configured directly
 * inside Rebuy's own dashboard — this session has no login to that
 * dashboard, so this file reproduces the same *outcome* (fields visible
 * in the live cart, written to the SAME cart attributes) from the theme
 * side instead, by watching for the Rebuy drawer to render and inserting
 * a matching block into it via JavaScript. It never touches GiftNote's
 * data model and never creates a second/parallel set of attributes.
 *
 * WHERE IT IS INSERTED
 * Immediately after #ilola-mip (Make It Personal / Physical
 * Presentation) and before #ilola-concierge (The Concierge Edit
 * cross-sell) inside .rebuy-cart__flyout — confirmed via live DOM
 * inspection of the current Smart Cart on 2026-09-16.
 *
 * THE SHIPPING NUDGE — business-day logic (updated 2026-09-16)
 * If the requested arrival date is close enough that it's at risk, a
 * plain-language note recommends Priority Shipping (or flags the date
 * as outside the normal window entirely) at checkout. This is a
 * SUGGESTION shown to the customer only — it cannot select a shipping
 * method or block checkout (that would require a Shopify checkout
 * extension, a separate, larger build).
 *
 * The thresholds are based on iLOLA's actual operational timing, not an
 * observed carrier quote:
 *   - Production: 2 business days (Day 1 begins the day AFTER the order
 *     is placed — i.e. production starts the next business day, not the
 *     order day itself).
 *   - Standard Shipping: 3-5 business days after dispatch
 *       -> Standard total fulfilment window: 5-7 business days
 *   - Priority Shipping: 1-3 business days after dispatch
 *       -> Priority total fulfilment window: 3-5 business days
 *
 * "Business days" excludes Saturdays and Sundays. No holiday-calendar
 * logic is applied (there is no reliable holiday source wired up here) —
 * a stated business-day count may run slightly long across a holiday,
 * which is a known, accepted limitation, not a bug.
 *
 * Counting convention: the number of business days "away" a requested
 * date is = the count of weekdays strictly after today, up to and
 * including the requested date (today itself is never counted, since
 * production begins the day after the order). A requested date that is
 * today or in the past counts as 0.
 *
 * Tiers (business days until the requested date):
 *   7+   -> no warning (comfortably inside Standard's own window)
 *   5-6  -> Standard is risky; recommend Priority
 *   3-4  -> Priority only, and even that is tight
 *   0-2  -> before iLOLA's normal production + delivery window entirely
 */
(function () {
  'use strict';

  // ---- Tunable constants -------------------------------------------------
  var ATTR_DATE = 'Gift Arrival Date';
  var ATTR_TIMING = 'Gift Timing';
  var ATTR_REQUEST = 'Gift Special Request';

  // Business-day tier boundaries. See header comment for how these map to
  // iLOLA's production + shipping windows. Tune here if operational
  // timing changes — no need to touch the counting logic below.
  var TIER_SAFE_MIN_DAYS = 7; // 7+ business days: no warning
  var TIER_STANDARD_RISK_MIN_DAYS = 5; // 5-6 business days: Standard is risky
  var TIER_PRIORITY_TIGHT_MIN_DAYS = 3; // 3-4 business days: Priority only, tight
  // Anything below TIER_PRIORITY_TIGHT_MIN_DAYS (i.e. 0-2 business days)
  // falls outside the normal production + delivery window entirely.

  var DEBOUNCE_MS = 500;
  var BLOCK_ID = 'ilola-gift-arrival-rebuy';
  var ANCHOR_ID = 'ilola-mip';

  // ---- Small helpers -------------------------------------------------
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root + 'cart.js' : '/cart.js');
  }

  function todayISO() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  }

  function isWeekend(d) {
    var day = d.getDay(); // 0 = Sunday, 6 = Saturday
    return day === 0 || day === 6;
  }

  // Business days between today (exclusive) and isoDate (inclusive),
  // excluding Saturdays and Sundays. Today or a past date returns 0.
  // No holiday-calendar logic — see header comment.
  function businessDaysUntil(isoDate) {
    if (!isoDate) return null;
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var target = new Date(isoDate + 'T00:00:00');
    if (isNaN(target.getTime())) return null;
    if (target <= today) return 0;

    var count = 0;
    var cursor = new Date(today);
    cursor.setDate(cursor.getDate() + 1);
    while (cursor <= target) {
      if (!isWeekend(cursor)) count++;
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  }

  // ---- Nudge copy -------------------------------------------------
  function nudgeMessage(days) {
    if (days === null) return '';
    if (days >= TIER_SAFE_MIN_DAYS) return '';
    if (days >= TIER_STANDARD_RISK_MIN_DAYS) {
      return 'Your requested date is approaching. Standard Shipping may not arrive in time. We recommend choosing Priority Shipping at checkout.';
    }
    if (days >= TIER_PRIORITY_TIGHT_MIN_DAYS) {
      return 'Your requested date is quite close. Please choose Priority Shipping at checkout. Even with Priority, timing may be tight, so add any important details in the Special Request above.';
    }
    return 'This date falls before our normal production and delivery window. We may not be able to guarantee arrival by this date. Please choose Priority Shipping and add a Special Request so our team can review it.';
  }

  // ---- Build the block -------------------------------------------------
  function buildBlock(values) {
    var wrap = document.createElement('div');
    wrap.id = BLOCK_ID;
    wrap.className = 'ilola-gift-arrival-rebuy';
    wrap.innerHTML =
      '<p class="ilola-gift-arrival-rebuy__eyebrow">Gift Timing</p>' +
      '<h2 class="ilola-gift-arrival-rebuy__heading">WHEN SHOULD IT ARRIVE?</h2>' +
      '<p class="ilola-gift-arrival-rebuy__intro">Tell us when you’d like your gift to arrive. We’ll do our best to accommodate your request. If your date falls outside our standard delivery window, expedited shipping may be required. If we don’t believe we can meet your requested date, our team will contact you.</p>' +
      '<div class="ilola-gift-arrival-rebuy__field">' +
        '<label for="IlolaGiftArrivalDate">Preferred arrival date</label>' +
        '<input type="date" id="IlolaGiftArrivalDate" data-gad-date>' +
      '</div>' +
      '<p class="ilola-gift-arrival-rebuy__nudge" data-gad-nudge hidden></p>' +
      '<fieldset class="ilola-gift-arrival-rebuy__field ilola-gift-arrival-rebuy__timing">' +
        '<legend>How important is this date?</legend>' +
        '<label class="ilola-gift-arrival-rebuy__radio">' +
          '<input type="radio" name="ilola-gad-timing" value="Around this date is fine" data-gad-timing>' +
          '<span>Around this date is fine</span>' +
        '</label>' +
        '<label class="ilola-gift-arrival-rebuy__radio">' +
          '<input type="radio" name="ilola-gad-timing" value="It needs to arrive by this date" data-gad-timing>' +
          '<span>It needs to arrive by this date</span>' +
        '</label>' +
      '</fieldset>' +
      '<div class="ilola-gift-arrival-rebuy__field">' +
        '<label for="IlolaGiftSpecialRequest">Special request (optional)</label>' +
        '<textarea id="IlolaGiftSpecialRequest" rows="2" data-gad-request></textarea>' +
      '</div>' +
      '<p class="ilola-gift-arrival-rebuy__disclaimer">For tighter deadlines, we may recommend an expedited shipping option at checkout.</p>';

    var dateField = wrap.querySelector('[data-gad-date]');
    var requestField = wrap.querySelector('[data-gad-request]');
    var nudgeEl = wrap.querySelector('[data-gad-nudge]');

    dateField.min = todayISO();
    if (values.date) dateField.value = values.date;
    if (values.request) requestField.value = values.request;
    if (values.timing) {
      var radios = wrap.querySelectorAll('[data-gad-timing]');
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === values.timing) radios[i].checked = true;
      }
    }
    updateNudge(nudgeEl, dateField.value);

    var persist = debounce(function () {
      var timingField = wrap.querySelector('[data-gad-timing]:checked');
      var dateValue = dateField.value;
      if (dateValue && dateValue < dateField.min) dateValue = '';

      var body = JSON.stringify({
        attributes: (function () {
          var a = {};
          a[ATTR_DATE] = dateValue;
          a[ATTR_TIMING] = timingField ? timingField.value : '';
          a[ATTR_REQUEST] = requestField.value;
          return a;
        })(),
      });

      fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body,
      });
    }, DEBOUNCE_MS);

    wrap.addEventListener('input', function (evt) {
      if (evt.target === dateField) updateNudge(nudgeEl, dateField.value);
      persist();
    });
    wrap.addEventListener('change', persist);

    return wrap;
  }

  function updateNudge(nudgeEl, dateValue) {
    var days = businessDaysUntil(dateValue);
    var msg = nudgeMessage(days);
    if (msg) {
      nudgeEl.textContent = msg;
      nudgeEl.hidden = false;
    } else {
      nudgeEl.textContent = '';
      nudgeEl.hidden = true;
    }
  }

  // ---- Styling (scoped, matches the /cart implementation's look) -------
  function injectStyle() {
    if (document.getElementById('ilola-gift-arrival-rebuy-style')) return;
    var style = document.createElement('style');
    style.id = 'ilola-gift-arrival-rebuy-style';
    style.textContent =
      '.ilola-gift-arrival-rebuy{padding:16px 0;margin:8px 0;border-top:1px solid rgba(0,0,0,0.08);border-bottom:1px solid rgba(0,0,0,0.08);}' +
      '.ilola-gift-arrival-rebuy__eyebrow{margin:0 0 4px;font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;opacity:.65;}' +
      '.ilola-gift-arrival-rebuy__heading{margin:0 0 8px;font-size:14px;letter-spacing:.03em;font-weight:600;}' +
      '.ilola-gift-arrival-rebuy__intro{margin:0 0 12px;font-size:12px;line-height:1.5;opacity:.85;}' +
      '.ilola-gift-arrival-rebuy__field{margin:0 0 12px;}' +
      '.ilola-gift-arrival-rebuy__field label,.ilola-gift-arrival-rebuy__timing legend{display:block;margin:0 0 6px;font-size:11.5px;font-weight:600;}' +
      '.ilola-gift-arrival-rebuy__field input[type="date"],.ilola-gift-arrival-rebuy__field textarea{width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid rgba(0,0,0,0.3);border-radius:6px;font:inherit;font-size:12.5px;}' +
      '.ilola-gift-arrival-rebuy__field textarea{resize:vertical;min-height:52px;}' +
      '.ilola-gift-arrival-rebuy__timing{margin:0 0 12px;padding:0;border:none;}' +
      '.ilola-gift-arrival-rebuy__radio{display:flex;align-items:center;gap:8px;margin:0 0 6px;font-size:12.5px;cursor:pointer;}' +
      '.ilola-gift-arrival-rebuy__radio input{flex:0 0 auto;width:16px;height:16px;margin:0;}' +
      '.ilola-gift-arrival-rebuy__disclaimer{margin:0;font-size:10.5px;opacity:.6;}' +
      '.ilola-gift-arrival-rebuy__nudge{margin:8px 0 12px;padding:9px 11px;font-size:11.5px;line-height:1.5;background:#fcf5f3;border:1px solid rgba(124,22,24,.25);border-radius:6px;color:#7c1618;}';
    document.head.appendChild(style);
  }

  // ---- Fetch current cart attributes to pre-fill on (re)render ---------
  function withCurrentAttributes(cb) {
    fetch(cartUrl())
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        cb({
          date: attrs[ATTR_DATE] || '',
          timing: attrs[ATTR_TIMING] || '',
          request: attrs[ATTR_REQUEST] || '',
        });
      })
      .catch(function () {
        cb({ date: '', timing: '', request: '' });
      });
  }

  // ---- Insert-if-missing, guarded against re-render churn ---------------
  var inserting = false;
  function ensureBlock() {
    if (inserting) return;
    var anchor = document.getElementById(ANCHOR_ID);
    if (!anchor || !anchor.parentNode) return;

    var existing = document.getElementById(BLOCK_ID);
    if (existing && existing.previousElementSibling === anchor) return; // already in place, right spot

    // Don't yank a block the customer is actively typing in.
    if (existing && existing.contains(document.activeElement)) return;

    if (existing) existing.remove();

    inserting = true;
    withCurrentAttributes(function (values) {
      injectStyle();
      var block = buildBlock(values);
      anchor.parentNode.insertBefore(block, anchor.nextSibling);
      inserting = false;
    });
  }

  // ---- Observe for the Rebuy drawer rendering/re-rendering --------------
  var observer = new MutationObserver(function () {
    ensureBlock();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Also try on load, in case the drawer is already present.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureBlock);
  } else {
    ensureBlock();
  }
})();
